<template>
  <table-wrapper>
    <v-data-table-server
      class="mb-10"
      :items="items"
      :headers="headers"
      :loading="loading"
      :fixed-header="true"
      :show-select="false"
      :items-per-page="pagination.pageSize"
      :items-length="totalItems"
      item-value="id"
      @update:options="handlePageChange"
    >
      <template #item="{ item, index }">
        <tr :id="`${index}`" :key="index" :data-testid="`url-row-${index}`">
          <td class="pl-4 pr-2">
            <span
              class="body2 text--semibold"
              :data-testid="`url-name-${index}`"
              v-text="item.urlName"
            />
          </td>
          <td class="d-flex justify-end">
            <v-btn
              icon="$deletedXs"
              :data-testid="`delete-btn-${index}`"
              @click="handleRemoveUrl(item)"
            />
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import { defineComponent, type PropType, toRef } from "vue";
import TableWrapper from "@/components/TableWrapper.vue";
import { VirtualOfficeAction } from "@/constants/network";
import type { VirtualOfficeURLListItem } from "@/_store/network/virtual-office.module";
import type { Pagination } from "@/types";
import { useI18n } from "vue-i18n";
import { useInfiniteScroll } from "@/composables/useInfiniteScroll";

export default defineComponent({
  components: {
    TableWrapper,
  },
  props: {
    items: { type: Array as PropType<VirtualOfficeURLListItem[]>, required: true },
    loading: { type: Boolean, default: false },
    totalItems: { type: Number, required: true },
    pagination: { type: Object as PropType<Pagination>, required: true },
  },

  emits: ["last-item-reached", "page-change", "remove-url"],

  setup(props, { emit }) {
    const i18n = useI18n();
    const handleRemoveUrl = (item: VirtualOfficeURLListItem) => {
      const payload = {
        item,
        action: item.excluded
          ? VirtualOfficeAction.REMOVE_FROM_EXCLUDELIST
          : VirtualOfficeAction.REMOVE_FROM_INCLUDELIST,
      };
      emit("remove-url", payload);
    };
    const handlePageChange = (payload: Pagination): void => {
      emit("page-change", payload);
    };
    const { onIntersect: handleVirtualScroll } = useInfiniteScroll(
      toRef(props, "items"),
      { total: toRef(props, "totalItems") },
      () => emit("last-item-reached")
    );

    const headers = [
      {
        title: i18n.t("network.virtualOffice.table.domainOrIp"),
        key: "urlName",
        sortable: false,
      },
      {},
    ];
    return {
      handlePageChange,
      handleRemoveUrl,
      handleVirtualScroll,
      headers,
    };
  },
});
</script>

<style scoped lang="scss">
.excluded-label {
  background: rgb(var(--v-theme-indigo-light));
  padding: 4px;
  color: rgb(var(--v-theme-primary));
  border-radius: 8px;
}

.included-label {
  background: rgb(var(--v-theme-primary));
  padding: 4px;
  color: white;
  border-radius: 8px;
}
</style>
